import { useRef, useState } from 'react';
import axios from 'axios';
import { uploadFile } from '../../helper/firebase';
import { triggerConfetti } from './confetti';

const MAX_PHOTO_BYTES = 15 * 1024 * 1024;

/**
 * State and behaviour for one kind of photo-backed shift action (check-in or
 * check-out) across every set in the Election Day widget: photo selection and
 * preview, the location permission flow with its "still proceed" fallback, and
 * upload → save → success. Instantiate it once per kind; every piece of state
 * is keyed by set id inside the instance, so the two kinds never collide.
 *
 * The caller owns the assignment records, the shared location modal, and the
 * shared in-flight map:
 *  - kind names this instance ('checkin' | 'checkout').
 *  - inFlight / setInFlight is a { [setId]: kind } map shared by both
 *    instances. While a set has any action in flight, neither instance will
 *    start another for that set: each save spreads the caller's current copy
 *    of the record, so two overlapping saves would race and the later one
 *    would overwrite the earlier one's fields in Quickbase.
 *  - getAssignment(setId) returns the current full record. It is spread into
 *    the save payload because Quickbase blanks any writable field it is not
 *    sent, so the whole record must always travel with the changes.
 *  - buildChanges({ imageUrl, coords }) returns the fields this action writes.
 *  - onSaved(setId, changes) lets the caller fold the written fields back into
 *    its records so a later action in the same session sends them forward.
 *  - requestLocationPrompt(setId) asks the caller to open the modal.
 *  - uploadFolder is the election's Firebase Storage folder for photos, as
 *    Quickbase holds it; uploadFile normalizes it and handles blank.
 *  - labels: { verb: 'check in', gerund: 'checking in', noun: 'Check-in' }.
 */
export default function useShiftPhotoAction({
  kind,
  labels,
  inFlight,
  setInFlight,
  getAssignment,
  buildChanges,
  onSaved,
  requestLocationPrompt,
  uploadFolder,
}) {
  const [success, setSuccess] = useState({});
  const [images, setImages] = useState({});
  const [imageError, setImageError] = useState({});
  const [locationError, setLocationError] = useState({});
  const [locationData, setLocationData] = useState({});
  const [skipLocation, setSkipLocation] = useState({});
  const fileInputRefs = useRef({});

  const patch = (setter, setId, value) => setter((prev) => ({ ...prev, [setId]: value }));

  // This instance's own action is in flight for the set.
  const isLoading = (setId) => inFlight[setId] === kind;
  // Any action, of either kind, is in flight for the set.
  const isBusy = (setId) => Boolean(inFlight[setId]);
  const start = (setId) => patch(setInFlight, setId, kind);
  const finish = (setId) =>
    setInFlight((prev) => {
      const next = { ...prev };
      delete next[setId];
      return next;
    });

  const registerFileInput = (setId, inputType) => (el) => {
    if (!fileInputRefs.current[setId]) {
      fileInputRefs.current[setId] = {};
    }
    fileInputRefs.current[setId][inputType] = el;
  };

  const triggerFileInput = (setId, inputType) => {
    const input = fileInputRefs.current[setId] && fileInputRefs.current[setId][inputType];
    if (input) input.click();
  };

  const handleFileSelect = (setId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      patch(setImageError, setId, 'Please select an image file (JPEG, PNG).');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      patch(setImageError, setId, 'Image must be less than 15MB.');
      return;
    }

    patch(setImageError, setId, null);
    patch(setLocationError, setId, null);
    patch(setSkipLocation, setId, false);
    patch(setImages, setId, { file, url: URL.createObjectURL(file) });
  };

  const removeImage = (setId) => {
    if (images[setId] && images[setId].url) {
      URL.revokeObjectURL(images[setId].url);
    }
    setImages((prev) => {
      const next = { ...prev };
      delete next[setId];
      return next;
    });
    patch(setImageError, setId, null);
    patch(setLocationError, setId, null);
  };

  // Fetch the position (unless told to skip it), then upload and save.
  const proceed = (setId, skipLocationCheck = false) => {
    if (isBusy(setId)) return;

    if (!skipLocationCheck && !navigator.geolocation) {
      patch(
        setLocationError,
        setId,
        `Geolocation is not supported by your browser or you have sharing blocked. You can still ${labels.verb} without sharing location.`
      );
      patch(setSkipLocation, setId, true);
      return;
    }

    start(setId);

    const save = async (coords = null) => {
      try {
        const assignment = getAssignment(setId);
        if (!assignment) {
          throw new Error('Set not found');
        }

        let imageUrl = '';
        if (images[setId] && images[setId].file) {
          try {
            const uploadResult = await uploadFile(images[setId].file, setId, uploadFolder);
            imageUrl = uploadResult.url;
          } catch (error) {
            console.error('Error uploading image:', error);
            throw new Error('Failed to upload image. Please try again.');
          }
        }

        const changes = buildChanges({ imageUrl, coords });
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saveAssignment`, {
          assignments: [{ ...assignment, ...changes }],
        });

        if (!response.data.success) {
          throw new Error(`${labels.noun} failed`);
        }

        onSaved(setId, changes);
        patch(setSuccess, setId, true);
        triggerConfetti();

        if (images[setId] && images[setId].url) {
          URL.revokeObjectURL(images[setId].url);
        }
      } catch (error) {
        console.error(`Error during ${labels.verb}:`, error);
        patch(setImageError, setId, `${labels.noun} failed: ${error.message || 'Unknown error'}`);
      } finally {
        finish(setId);
      }
    };

    if (skipLocationCheck) {
      save();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        patch(setLocationData, setId, coords);
        await save(coords);
      },
      (error) => {
        console.error('Geolocation error:', error);
        patch(setLocationError, setId, `Location access failed, but you can still ${labels.verb}.`);
        patch(setSkipLocation, setId, true);
        // Nothing is in flight any more; leave the action button usable.
        finish(setId);
      }
    );
  };

  // Entry point for the action button: require a photo, then route on the
  // browser's location permission state.
  const request = (setId) => {
    if (isBusy(setId)) return;

    if (!images[setId] || !images[setId].file) {
      patch(setImageError, setId, `Please upload a photo before ${labels.gerund}.`);
      return;
    }

    patch(setImageError, setId, null);
    patch(setLocationError, setId, null);

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          if (result.state === 'granted') {
            proceed(setId);
          } else if (result.state === 'denied') {
            patch(
              setLocationError,
              setId,
              `Location sharing is blocked in your browser settings. To enable: update your settings, refresh, and try ${labels.gerund} again. You can still ${labels.verb} without sharing location.`
            );
            patch(setSkipLocation, setId, true);
          } else {
            requestLocationPrompt(setId);
          }
        })
        .catch(() => {
          requestLocationPrompt(setId);
        });
    } else {
      requestLocationPrompt(setId);
    }
  };

  const skipLocationAndProceed = (setId) => {
    patch(setSkipLocation, setId, true);
    proceed(setId, true);
  };

  return {
    isLoading,
    isBusy,
    success,
    images,
    imageError,
    locationError,
    locationData,
    skipLocation,
    registerFileInput,
    triggerFileInput,
    handleFileSelect,
    removeImage,
    request,
    proceed,
    skipLocationAndProceed,
  };
}
